// Pulse / CRM section — customer counts + stage breakdown.

import React, { useContext } from 'react';
import {
  Box,
  ButtonBase,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { Link as RouterLink } from 'react-router-dom';
import SectionShell from './SectionShell';
import KpiTile from './KpiTile';
import { BusinessContext } from '../../../contexts/BusinessContext';

const STAGE_LABEL = {
  lead: 'Leads',
  contacted: 'Contacted',
  qualified: 'Qualified',
  customer: 'Customers',
  repeat: 'Repeat',
  lost: 'Lost',
};

const STAGE_COLOR = {
  lead: 'grey.500',
  contacted: 'info.main',
  qualified: 'primary.main',
  customer: 'success.main',
  repeat: 'secondary.main',
  lost: 'error.light',
};

export default function CrmSection({ crm }) {
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const crmPath = (stage) =>
    stage ? `${basePath}/crm?stage=${stage}` : `${basePath}/crm`;

  if (!crm) {
    return (
      <SectionShell
        title="Customers"
        subtitle="Pipeline & stages"
        icon={<GroupOutlinedIcon />}
        empty
        emptyCopy="Add your first customer to start tracking your pipeline."
      />
    );
  }

  const totalForStages = crm.stageBreakdown.reduce((s, x) => s + x.count, 0);

  return (
    <SectionShell
      title="Customers"
      subtitle="Pipeline & engagement"
      icon={<GroupOutlinedIcon />}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2,
        }}
      >
        <ButtonBase
          component={RouterLink}
          to={crmPath()}
          sx={{ display: 'block', textAlign: 'left', borderRadius: 2 }}
        >
          <KpiTile value={crm.totalCustomers} label="Total" />
        </ButtonBase>
        <ButtonBase
          component={RouterLink}
          to={crmPath('lead')}
          sx={{ display: 'block', textAlign: 'left', borderRadius: 2 }}
        >
          <KpiTile
            value={crm.newThisMonth}
            label="New this month"
            accent="success.main"
          />
        </ButtonBase>
        <ButtonBase
          component={RouterLink}
          to={crmPath()}
          sx={{ display: 'block', textAlign: 'left', borderRadius: 2 }}
        >
          <KpiTile value={crm.activeLast30} label="Active 30d" />
        </ButtonBase>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
            mb: 1,
          }}
        >
          By stage
        </Typography>
        <Stack spacing={1.25}>
          {crm.stageBreakdown.map((s) => {
            const pct = totalForStages
              ? Math.round((s.count / totalForStages) * 100)
              : 0;
            return (
              <ButtonBase
                key={s.stage}
                component={RouterLink}
                to={crmPath(s.stage)}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {STAGE_LABEL[s.stage] || s.stage}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 12,
                    }}
                  >
                    {s.count} · {pct}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: STAGE_COLOR[s.stage] || 'primary.main',
                      borderRadius: 999,
                    },
                  }}
                />
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    </SectionShell>
  );
}
